import { useMemo } from 'react';
import { uniqBy, uniqueId } from 'lodash';

import { isAPStyleGuideFormat } from 'src/data/format';
import { PROMPT_GENERIC_INSTRUCTION, PROMPT_INSTRUCTION_TYPE } from 'src/data/generation';
import { isToolSupportSectionHeader, isToolSupportSectionStyle } from 'src/data/generationTool';
import { useBrandVoicesAssets } from 'src/store/brandVoices';

const SECTION_HEADER_INSTRUCTION = {
  id: uniqueId('generic_'),
  type: PROMPT_INSTRUCTION_TYPE.generic,
  instruction: PROMPT_GENERIC_INSTRUCTION.sectionHeader,
  text: 'Add section header',
};

const HEADING_INSTRUCTIONS = [
  {
    id: uniqueId('generic_'),
    type: PROMPT_INSTRUCTION_TYPE.generic,
    instruction: PROMPT_GENERIC_INSTRUCTION.h1,
    text: 'Use H1 styling for this section',
  },
  {
    id: uniqueId('generic_'),
    type: PROMPT_INSTRUCTION_TYPE.generic,
    instruction: PROMPT_GENERIC_INSTRUCTION.h2,
    text: 'Use H2 styling for this section',
  },
  {
    id: uniqueId('generic_'),
    type: PROMPT_INSTRUCTION_TYPE.generic,
    instruction: PROMPT_GENERIC_INSTRUCTION.h3,
    text: 'Use H3 styling for this section',
  },
];

const getGenericInstructions = (generationTool) => {
  if (!isToolSupportSectionStyle(generationTool)) {
    return [];
  }

  return isToolSupportSectionHeader(generationTool)
    ? [SECTION_HEADER_INSTRUCTION, ...HEADING_INSTRUCTIONS]
    : HEADING_INSTRUCTIONS;
};

/**
 * Get a list of structure instructions for the prompt
 * @param {GENERATION_TOOL} generationTool - Generation tool
 * @param {LANGUAGE} language - Language to use when listing brand voices
 * @param {PROMPT_INSTRUCTION_TYPE[]} instructionTypes - Which instruction types to display
 * @returns {{id: string, label: string, instruction: PromptInstruction}[]}
 */
export const usePromptFieldStructureInstructions = ({
  generationTool,
  language,
  instructionTypes,
}) => {
  const { brandVoicesAssets, customFormulas, customFormats } = useBrandVoicesAssets({ language });

  const brandVoicesWithTerms = useMemo(
    () => brandVoicesAssets.filter((voice) => voice.brandTerms?.length > 0),
    [brandVoicesAssets]
  );

  return useMemo(
    () =>
      preparePromptFieldStructureInstructions({
        generationTool,
        brandVoicesWithTerms,
        customFormulas,
        customFormats,
        instructionTypes,
      }),
    [generationTool, brandVoicesWithTerms, customFormulas, customFormats, instructionTypes]
  );
};

export const preparePromptFieldStructureInstructions = ({
  generationTool,
  brandVoicesWithTerms,
  customFormulas,
  customFormats,
  instructionTypes = [],
}) => {
  const genericInstructions = instructionTypes.includes(PROMPT_INSTRUCTION_TYPE.generic)
    ? getGenericInstructions(generationTool)
    : [];

  const vocabularyInstructions = instructionTypes.includes(PROMPT_INSTRUCTION_TYPE.vocabulary)
    ? brandVoicesWithTerms.map(createVocabularyInstruction)
    : [];

  const preparedFormats = filterFormats(customFormats);
  const formatInstructions = instructionTypes.includes(PROMPT_INSTRUCTION_TYPE.format)
    ? preparedFormats.map(createFormatInstruction)
    : [];

  const preparedFormulas = filterFormulas({ customFormulas, generationTool });
  const formulaInstructions = instructionTypes.includes(PROMPT_INSTRUCTION_TYPE.formula)
    ? preparedFormulas.map(createFormulaInstruction)
    : [];

  return [
    ...genericInstructions,
    ...vocabularyInstructions,
    ...formatInstructions,
    ...formulaInstructions,
  ];
};

const createVocabularyInstruction = (brandVoice) => ({
  id: brandVoice.id,
  type: PROMPT_INSTRUCTION_TYPE.vocabulary,
  text: `Use brand vocabulary from: ${brandVoice.name}`,
});

const createFormatInstruction = (format) => ({
  id: format.id,
  type: PROMPT_INSTRUCTION_TYPE.format,
  text: `Use format: ${format.name}`,
});

const createFormulaInstruction = (formula) => ({
  id: formula.id,
  type: PROMPT_INSTRUCTION_TYPE.formula,
  text: `Use formula: ${formula.name}`,
});

// Only take formulas that are supported by the current generation tool
const filterFormulas = ({ customFormulas, generationTool }) =>
  customFormulas.filter((formula) => formula.generationTool === generationTool);

// Only one "AP style guide" should be shown in the list
// (it is auto-generated - we use "ap-style-guide" as a unique identifier just for filtering)
const filterFormats = (customFormats) =>
  uniqBy(customFormats, (format) => (isAPStyleGuideFormat(format) ? 'ap-style-guide' : format.id));
